/**
 * Optimistic Cart Updates
 * Adds items to cart UI immediately without waiting for API
 * Provides instant, snappy experience for cross-sell adds
 */

class OptimisticCartManager {
    constructor() {
        this.pendingItems = new Map(); // Track optimistic items
        this.init();
    }
    
    init() {
        // Intercept product form submissions from mini-cart recommendations
        document.addEventListener('submit', (e) => {
            const form = e.target;
            
            // Only handle forms that are product-forms inside mini-cart
            if (form.matches('form[is="product-form"]') && 
                form.closest('.mini-cart__recommendations')) {
                e.preventDefault();
                e.stopImmediatePropagation(); // Prevent ProductForm handler from also firing
                this.handleOptimisticAdd(form);
                return false;
            }
        }, true); // Use capture phase to intercept early
        
        console.log('[OptimisticCart] Manager initialized');
    }
    
    /**
     * Handle optimistic cart add from cross-sell
     */
    async handleOptimisticAdd(form) {
        try {
            // Extract product data from form
            const formData = new FormData(form);
            const variantId = formData.get('id');
            const sellingPlan = formData.get('selling_plan');
            const quantity = parseInt(formData.get('quantity') || '1');
            
            // Get product details from the DOM
            const productData = this.extractProductData(form);
            
            if (!productData) {
                console.error('[OptimisticCart] Could not extract product data');
                return;
            }
            
            console.log('[OptimisticCart] Adding item optimistically:', productData);
            
            // Generate temporary ID for tracking
            const tempId = `optimistic-${Date.now()}-${variantId}`;
            this.pendingItems.set(tempId, { variantId, productData });
            
            // 1. Add optimistic line item to DOM immediately
            this.addOptimisticLineItem(tempId, productData, quantity, sellingPlan);
            
            // 2. Update shipping bar immediately
            this.updateShippingBarOptimistically(productData.price * quantity);
            
            // 3. Update cart count immediately
            this.updateCartCountOptimistically(quantity);
            
            // 4. Show visual feedback
            const button = form.querySelector('button[type="submit"]');
            this.showAddedFeedback(button);
            
            // 5. Now make actual API call in background
            this.submitToShopify(form, tempId, variantId, sellingPlan, quantity);
            
        } catch (error) {
            console.error('[OptimisticCart] Error handling optimistic add:', error);
        }
    }
    
    /**
     * Extract product data from form's parent elements
     */
    extractProductData(form) {
        const container = form.closest('.recommendations-slider__content-container');
        if (!container) return null;
        
        const titleEl = container.querySelector('.recommendations-slider__title');
        const variantEl = container.querySelector('.recommendations-slider__variant-title');
        const imageEl = container.querySelector('.recommendations-slider__product-image img');
        const priceText = form.querySelector('.recommendations-slider__button')?.textContent || '';
        
        // Extract price from button text (format: "Add - $XX.XX")
        const priceMatch = priceText.match(/\$[\d,]+\.?\d*/);
        const priceString = priceMatch ? priceMatch[0].replace(/[$,]/g, '') : '0';
        const price = parseFloat(priceString) * 100; // Convert to cents
        
        return {
            title: titleEl?.textContent?.trim() || 'Product',
            variant: variantEl?.textContent?.trim() || '',
            image: imageEl?.src || '',
            price: price,
            formattedPrice: priceMatch ? priceMatch[0] : '$0'
        };
    }
    
    /**
     * Add optimistic line item to cart DOM
     */
    addOptimisticLineItem(tempId, productData, quantity, sellingPlan) {
        const cartForm = document.getElementById('mini-cart-form');
        if (!cartForm) return;
        
        // Create line item HTML
        const lineItemHtml = `
            <athena-line-item data-optimistic-id="${tempId}" class="cart-item-adding">
                <div class="athena-line-item">
                    <div class="athena-line-item__image-container">
                        <a href="#" class="athena-line-item__image-wrapper" tabindex="-1" aria-hidden="true">
                            <span class="line-item__loader" hidden>
                                <span class="line-item__loader-spinner spinner" hidden>
                                    <svg width="16" height="16" viewBox="0 0 16 16">
                                        <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="2" opacity="0.3"/>
                                        <path d="M8 1 A7 7 0 0 1 15 8" fill="none" stroke="currentColor" stroke-width="2"/>
                                    </svg>
                                </span>
                                <span class="line-item__loader-mark" hidden>✓</span>
                            </span>
                            ${productData.image ? `<img src="${productData.image}" class="line-item__image" loading="lazy">` : ''}
                        </a>
                    </div>
                    <div class="athena-line-item__item-info-container">
                        <div class="athena-line-item__row athena-line-item__first-row">
                            <a href="#" class="athena-line-item__title">${productData.title}</a>
                            <div class="athena-line-item__price">
                                <span class="price price--highlight">${productData.formattedPrice}</span>
                            </div>
                        </div>
                        ${productData.variant ? `
                        <div class="athena-line-item__row athena-line-item__second-row">
                            <div class="athena-line-item__item-meta-property-list">
                                <span class="product-item-meta__property text--subdued text--xsmall athena-line-item__variant-title">
                                    ${productData.variant}
                                </span>
                            </div>
                        </div>
                        ` : ''}
                        <div class="athena-line-item__row athena-line-item__fourth-row">
                            <line-item-quantity class="athena-line-item__quantity">
                                <span class="text--subdued text--xxsmall" style="opacity: 0.6;">Adding...</span>
                            </line-item-quantity>
                        </div>
                    </div>
                </div>
            </athena-line-item>
        `;
        
        // Insert at the beginning of the form (will appear at TOP of cart)
        cartForm.insertAdjacentHTML('afterbegin', lineItemHtml);
        
        // Trigger animation by forcing reflow
        const newItem = cartForm.querySelector(`[data-optimistic-id="${tempId}"]`);
        if (newItem) {
            newItem.offsetHeight; // Force reflow
        }
    }
    
    /**
     * Update shipping bar optimistically
     */
    updateShippingBarOptimistically(addedPrice) {
        const shippingBar = document.querySelector('free-shipping-bar');
        if (!shippingBar) return;
        
        // Get current threshold
        const threshold = parseFloat(shippingBar.getAttribute('threshold')) || 3500;
        
        // Calculate new total (get current from subtotal)
        const subtotalEl = document.querySelector('.mini-cart__subtotal-amount');
        const currentTotal = this.parseMoneyString(subtotalEl?.textContent || '$0');
        const newTotal = currentTotal + addedPrice;
        
        // Update progress
        const progress = Math.min(newTotal / threshold, 1);
        shippingBar.style.setProperty('--progress', progress);
        
        // Update text if needed
        if (newTotal >= threshold) {
            const textEl = shippingBar.querySelector('.shipping-bar__text');
            if (textEl && !textEl.textContent.includes('Free shipping')) {
                textEl.textContent = 'Free shipping unlocked!';
            }
        }
    }
    
    /**
     * Update cart count optimistically
     */
    updateCartCountOptimistically(quantity) {
        const cartCounts = document.querySelectorAll('cart-count');
        cartCounts.forEach(countEl => {
            const current = parseInt(countEl.textContent) || 0;
            countEl.textContent = current + quantity;
            countEl.classList.add('updating');
            setTimeout(() => countEl.classList.remove('updating'), 250);
        });
    }
    
    /**
     * Show visual feedback on button
     */
    showAddedFeedback(button) {
        if (!button) return;
        
        const originalText = button.textContent;
        button.textContent = 'Added!';
        button.style.background = '#E23185';  // Athena Club pink
        button.style.borderColor = '#E23185';
        button.style.color = '#ffffff';  // White text
        
        setTimeout(() => {
            button.textContent = originalText;
            button.style.background = '';
            button.style.borderColor = '';
            button.style.color = '';
        }, 1500);
    }
    
    /**
     * Submit to Shopify API and replace optimistic item with real data
     */
    async submitToShopify(form, tempId, variantId, sellingPlan, quantity) {
        try {
            const formData = new FormData(form);
            
            const response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
                method: 'POST',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: formData
            });
            
            if (!response.ok) {
                throw new Error('Failed to add to cart');
            }
            
            const responseJson = await response.json();
            
            // Get updated cart data
            const cartResponse = await fetch(`${window.themeVariables.routes.cartUrl}.js`);
            const cartContent = await cartResponse.json();
            
            // Dispatch cart events
            document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
                bubbles: true,
                detail: { cart: cartContent }
            }));
            
            // Get section HTML for proper rendering
            const sectionId =
                document.querySelector('cart-drawer')?.getAttribute('section') ||
                document.getElementById('mini-cart-page')?.dataset.sectionId;
            const sectionResponse = await fetch(`${window.themeVariables.routes.cartUrl}?section_id=${sectionId}`);
            const sectionHTML = await sectionResponse.text();

            // Key by sectionId so the right element picks it up regardless of context
            cartContent["sections"] = { [sectionId]: sectionHTML };
            
            document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
                bubbles: true,
                detail: {
                    cart: cartContent,
                    optimisticId: tempId // Pass the temp ID so we can handle the replacement
                }
            }));
            
            // Remove from pending
            this.pendingItems.delete(tempId);
            
            console.log('[OptimisticCart] Real data received, cart updated');
            
        } catch (error) {
            console.error('[OptimisticCart] API call failed:', error);
            // Remove the optimistic item on error
            this.removeOptimisticItem(tempId);
            // Silently fail - optimistic item will be removed, user can try again
        }
    }
    
    /**
     * Remove optimistic item (on error)
     */
    removeOptimisticItem(tempId) {
        const item = document.querySelector(`[data-optimistic-id="${tempId}"]`);
        if (item) {
            item.classList.add('cart-item-removing');
            setTimeout(() => item.remove(), 500);
        }
        this.pendingItems.delete(tempId);
    }
    
    /**
     * Parse money string to cents
     */
    parseMoneyString(moneyStr) {
        const cleaned = moneyStr.replace(/[$,]/g, '');
        const value = parseFloat(cleaned) || 0;
        return Math.round(value * 100);
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        window.optimisticCart = new OptimisticCartManager();
    });
} else {
    window.optimisticCart = new OptimisticCartManager();
}

// Export for use in other scripts
window.OptimisticCartManager = OptimisticCartManager;

